import React, { type FunctionComponent, useRef, useState } from 'react';
import noop from 'lodash/noop';

import { I18n } from 'common/i18n';
import { userCanChangeAudience } from 'common/components/AccessManager/Util';
import { getCurrentUser } from 'common/current_user';
import { ForgeButton, ForgePopover } from '@tylertech/forge-react';
import { isMobileOrTablet } from 'common/visualizations/helpers/MediaQueryHelper';

const getActionTextKey = (previouslyPublished: boolean) => {
  if (previouslyPublished) {
    return 'previously_published';
  } else {
    return 'never_published';
  }
};

interface PublishButtonProps {
  previouslyPublished: boolean;
  onClick: () => void;
  disabled?: boolean;
  disabledReason?: string | JSX.Element;
}

export const PublishButton: FunctionComponent<PublishButtonProps> = ({
  previouslyPublished = false,
  disabled = false,
  disabledReason,
  onClick = noop,
}: PublishButtonProps) => {
  const targetRef = useRef<any>();
  const [flyoutOpen, setFlyoutOpen] = useState(false);

  const actionTextKey = getActionTextKey(previouslyPublished);

  const userCannotChangeAudience = !userCanChangeAudience(getCurrentUser());

  // button is disabled if user cannot change audience and is publishing for the first time
  const updateDisabledDueToUserAudiencePrivileges =
    userCannotChangeAudience && actionTextKey != 'previously_published';
  const buttonIsDisabled = updateDisabledDueToUserAudiencePrivileges || disabled;

  // determine the action
  const key = getActionTextKey(previouslyPublished);
  const actionText = I18n.t(
    `shared.components.asset_action_bar.publication_action.draft.primary_action_text.${key}`,
  );

  const getDisabledExplanation = () => {
    if (userCannotChangeAudience) {
      return I18n.t('shared.components.asset_action_bar.publication_action.publish_disabled');
    }
    return disabledReason;
  };
  const onClickAction = buttonIsDisabled ? noop : onClick;

  if (isMobileOrTablet()) {
    return (
      <button
        onClick={onClickAction}
        disabled={buttonIsDisabled}
        data-testid="aab-publish-button"
        className="mobile-action-item"
      >
        {actionText}
      </button>
    );
  }

  const disabledExplanation = getDisabledExplanation();

  const onMouseLeave = () => {
    // when clicking 'fix metadata' button this ensures the popover closes
    if (targetRef.current) {
      targetRef.current.hideAsync();
    }
  };

  return (
    <div onMouseLeave={onMouseLeave}>
      <ForgeButton
        onClick={onClickAction}
        variant="raised"
        data-testid="aab-publish-button"
        id="aab-publish-button"
        disabled={buttonIsDisabled}
      >
        {actionText}
      </ForgeButton>
      {buttonIsDisabled && disabledExplanation && (
        <ForgePopover
          triggerType="hover"
          anchor="aab-publish-button"
          ref={targetRef}
          data-testid="aab-publish-button-popover"
        >
          <div className="forge-typography--body1">
            <div className="publish-button-flyout-content">{disabledExplanation}</div>
          </div>
        </ForgePopover>
      )}
    </div>
  );
};
